import React, { useEffect, useRef, useState } from "react";
import { Formik, Form, FormikProps, Field, useFormikContext } from "formik";
import * as Yup from "yup";

import ActionFormButton from "../../../common/ActionFormButton";
import {
  renderRowWithTwoItems,
  renderRowWithSingleItem,
  renderSectionTitle,
  getRowItemObject,
  Section,
  Wrapper,
  renderRowWithSingleCheckbox,
  Row,
  RowItem,
} from "../../../common/FormUtils";
import { PracticeType, Habit } from "../../../../types/Habit";
import FullScreenDialog from "../../../common/FullScreenDialog";
import ActionForm, { SubactionForm } from "../components/actionForm";
import { Action, ActionType, Subaction } from "../../../../types/ActionType";
import ActionButton from "../../../common/ActionButton";
import Table from "../../../common/table";
import actionTabConfig from "../components/actionForm/config";
import { getFirebaseKey } from "../../../../database/utils/getFirebaseKey";
import ImportHabits from "../components/ImportHabits";
import ImportActions from "../../events/components/ImportAction";
import ImportSubactions from "../components/ImportSubactions";
import WorkoutBuilderConfig from "../components/WorkoutBuilderConfig";
import {
  Avatar,
  Badge,
  Box,
  Chip,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@material-ui/core";
import VideocamIcon from "@material-ui/icons/Videocam";
import SearchIcon from "@material-ui/icons/Search";
import ImageUpload from "../../../common/ImageUpload";
import { firestore } from "../../../../database/firebase";
import DB_PATH from "../../../../database/DB_PATH";
import { Plan } from "../../../../types/Plan";
import Dialog from "../../../common/Dialog";
import { cloneWorkoutBuilderConfig } from "../../../../types/WorkoutBuilder";

const cloneSubaction = (subaction: Subaction): Subaction => ({
  ...subaction,
  tags: subaction.tags ? [...subaction.tags] : subaction.tags,
  equipmentNeeded: subaction.equipmentNeeded
    ? [...subaction.equipmentNeeded]
    : subaction.equipmentNeeded,
  bodyRegions: subaction.bodyRegions
    ? [...subaction.bodyRegions]
    : subaction.bodyRegions,
  movementQualities: subaction.movementQualities
    ? [...subaction.movementQualities]
    : subaction.movementQualities,
  compensationsAddressed: subaction.compensationsAddressed
    ? [...subaction.compensationsAddressed]
    : subaction.compensationsAddressed,
  userUseCases: subaction.userUseCases
    ? [...subaction.userUseCases]
    : subaction.userUseCases,
  ingredients: subaction.ingredients
    ? subaction.ingredients.map((ingredient) => ({ ...ingredient }))
    : subaction.ingredients,
});

const StandaloneSubactionsLoader = ({
  habitId,
  communityId,
}: {
  habitId: string;
  communityId: string;
}) => {
  const { setFieldValue } = useFormikContext<Habit>();
  const lastHabitIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!habitId || habitId === lastHabitIdRef.current) return;
    lastHabitIdRef.current = habitId;

    let isActive = true;

    const loadSubactions = async () => {
      const snapshot = await firestore
        .collection(DB_PATH.HABIT_SUBACTION)
        .where("habitId", "==", habitId)
        .get();

      if (!isActive) return;

      const subactions = snapshot.docs.map(
        (doc) => ({ id: doc.id, ...doc.data() } as Subaction)
      );
      const standalone = subactions.filter((subaction) => !subaction.actionId);

      setFieldValue(
        "standaloneSubactions",
        standalone.map(cloneSubaction)
      );

      if (communityId) {
        const backfillPromises = snapshot.docs
          .filter((doc) => !doc.data()?.communityId)
          .map((doc) =>
            doc.ref.set({ communityId }, { merge: true })
          );
        if (backfillPromises.length > 0) {
          await Promise.all(backfillPromises);
        }
      }
    };

    loadSubactions().catch((error) => {
      console.error("Failed to load standalone subactions", error);
    });

    return () => {
      isActive = false;
    };
  }, [habitId, communityId, setFieldValue]);

  return null;
};

const ActionsLoader = ({
  habitId,
  communityId,
}: {
  habitId: string;
  communityId: string;
}) => {
  const { setFieldValue } = useFormikContext<Habit>();
  const lastHabitIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!habitId || habitId === lastHabitIdRef.current) return;
    lastHabitIdRef.current = habitId;

    let isActive = true;

    const loadActions = async () => {
      const actionsSnapshot = await firestore
        .collection(DB_PATH.HABIT_ACTION)
        .where("habitId", "==", habitId)
        .get();

      if (!isActive) return;

      const subactionsSnapshot = await firestore
        .collection(DB_PATH.HABIT_SUBACTION)
        .where("habitId", "==", habitId)
        .get();

      if (!isActive) return;

      const allSubactions = subactionsSnapshot.docs.map(
        (doc) => ({ id: doc.id, ...doc.data() } as Subaction)
      );

      const actions: Action[] = actionsSnapshot.docs.map((doc) => {
        const action = { id: doc.id, ...doc.data() } as Action;
        action.subactions = allSubactions.filter((s) => s.actionId === action.id);
        return action;
      });

      if (actions.length > 0) {
        setFieldValue("actions", actions.map(cloneAction));
      }
    };

    loadActions().catch((error) => {
      console.error("Failed to load actions from Actions collection", error);
    });

    return () => {
      isActive = false;
    };
  }, [habitId, communityId, setFieldValue]);

  return null;
};

const cloneAction = (action: Action): Action => ({
  ...action,
  id: action.id || getFirebaseKey(),
  subactions: (action.subactions || []).map(cloneSubaction),
});
const useStyles = makeStyles((theme) => ({
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
    maxWidth: 300,
  },
  chips: {
    display: "flex",
    flexWrap: "wrap",
  },
  chip: {
    margin: 2,
  },
}));

const practiceTypeOptions = (
  Object.values(PracticeType).filter(
    (value) => typeof value === "number"
  ) as PracticeType[]
).map((type) => ({
  value: type,
  label: PracticeType[type].toString().replace(/_/g, " "),
}));
type Props = {
  onChageFormToEditMode: Function;
  onRemoveItem: Function;
  item: Habit;
  mode: string;
  onCreateItem: (habit: Habit) => void;
  onEditItem: (habit: Habit) => void;
};

type SubactionContext = {
  actionId?: string;
  subactionId?: string;
  subactionIndex?: number;
  isStandalone?: boolean;
};

const subactionTabConfig = [
  {
    columnTitle: "",
    dataField: ["component"],
    component: ({ values }) => {
      const avatar = <Avatar src={values.backgroundImageURL} variant="rounded" />;
      if (values.videoURL) {
        return (
          <Badge
            overlap="rectangular"
            badgeContent={
              <VideocamIcon
                style={{
                  fontSize: 14,
                  color: "#fff",
                  backgroundColor: "#1976d2",
                  borderRadius: 4,
                  padding: 2,
                }}
              />
            }
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          >
            {avatar}
          </Badge>
        );
      }
      return avatar;
    },
  },
  {
    columnTitle: "Action",
    dataField: ["actionName"],
  },
  {
    columnTitle: "Title",
    dataField: ["name"],
  },
  {
    columnTitle: "Description",
    dataField: ["description"],
  },
  {
    columnTitle: "Type",
    dataField: ["actionType"],
    transform: (value) =>
      value ? value.toString().replace(/_/g, " ") : "",
  },
];

const HabitForm: React.FC<Props> = ({
  onChageFormToEditMode,
  onRemoveItem,
  item,
  mode,
  onCreateItem,
  onEditItem,
}) => {
  const [isActionFormOpen, setIsActionFormOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState<Action | null>(null);
  const [learningPlans, setLearningPlans] = useState<Plan[]>([]);
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false);
  const [actionSelectedToRemove, setActionSelectedToRemove] =
    useState<Action | null>(null);
  const [currentTabIndex, setCurrentTabIndex] = useState(0);
  const [subactionSearch, setSubactionSearch] = useState("");
  const [subactionPage, setSubactionPage] = useState(0);
  const [subactionRowsPerPage, setSubactionRowsPerPage] = useState(10);
  const [isSubactionFormOpen, setIsSubactionFormOpen] = useState(false);
  const [subactionFormMode, setSubactionFormMode] = useState<
    "create" | "edit"
  >("create");
  const [selectedSubaction, setSelectedSubaction] =
    useState<Subaction | null>(null);
  const [selectedSubactionContext, setSelectedSubactionContext] =
    useState<SubactionContext | null>(null);
  const [isSubactionRemoveDialogOpen, setIsSubactionRemoveDialogOpen] =
    useState(false);
  const [subactionSelectedToRemove, setSubactionSelectedToRemove] =
    useState<SubactionContext | null>(null);
  const classes = useStyles();
  const workoutBuilderUploadIdRef = useRef(item.id || getFirebaseKey());
  const normalizedPracticeType =
    typeof item.practiceType === "number"
      ? item.practiceType
      : typeof item.practiceType === "string" &&
        !Number.isNaN(Number(item.practiceType))
      ? Number(item.practiceType)
      : PracticeType.DEFAULT;

  useEffect(() => {
    const fetchLearningPlans = async () => {
      if (!item.communityId) return;
      const plansSnapshot = await firestore
        .collection(DB_PATH.PLAN)
        .where("communityId", "==", item?.communityId)
        .get();
      const plansData = plansSnapshot.docs.map((doc) => ({
        id: doc.id,
        title: doc.data().title,
      }));
      //@ts-ignore
      setLearningPlans(plansData);
    };

    fetchLearningPlans();
  }, []);
  const onToggleActionForm = () => {
    setIsActionFormOpen(!isActionFormOpen);
    if (isActionFormOpen) {
      setSelectedAction(null);
    }
  };

  const initialValues: Habit = {
    id: item.id || "",
    habitName: item.habitName || "",
    habitDescription: item.habitDescription || "",
    targetFrequency: item.targetFrequency || "",
    backgroundImageURL: item.backgroundImageURL || "",
    practiceType: normalizedPracticeType,
    position: (item.position ?? "") as any,
    status: item.status || true,
    actions: (item.actions || []).map(cloneAction),
    authorsIds: item.authorsIds || [],
    communityId: item.communityId || "",
    creationDate: item.creationDate || new Date().toISOString(),
    learningPlansIds: item.learningPlansIds || [],
    levels: item.levels || [],
    standaloneSubactions: [],
    useLibrary: item.useLibrary ?? false,
    vectorStoreId: item.vectorStoreId || "",
    vectorStoreItemCount: item.vectorStoreItemCount,
    vectorStoreLastRebuild: item.vectorStoreLastRebuild,
    generationPlanPrompt: item.generationPlanPrompt || "",
    defaultDietType: item.defaultDietType || "",
    workoutBuilder: (
      item.workoutBuilder || normalizedPracticeType === PracticeType.EXERCISE
    )
      ? cloneWorkoutBuilderConfig(item.workoutBuilder)
      : undefined,
  };

  const validationSchema = Yup.object().shape({
    habitName: Yup.string().required("Habit name is required"),
    habitDescription: Yup.string(),
  });

  const renderForm = (formikProps: FormikProps<Habit>) => {
    console.log("Values", formikProps.values);

    const onCreateAction = (newAction: Omit<Action, "id">) => {
      setIsActionFormOpen(false);

      console.log("Creating Action", newAction);
      const databaseKey = getFirebaseKey();
      const actionWithId: Action = { ...newAction, id: databaseKey };
      const updatedActions = [
        ...(formikProps.values.actions || []).map(cloneAction),
        cloneAction(actionWithId),
      ];
      formikProps.setFieldValue("actions", updatedActions);
    };

    const onEditAction = (updatedAction: Action) => {
      console.log("Edited Action", updatedAction);
      setIsActionFormOpen(false);

      const updatedActions = formikProps.values.actions.map((action) =>
        action.id === updatedAction.id
          ? cloneAction(updatedAction)
          : cloneAction(action)
      );
      formikProps.setFieldValue("actions", updatedActions);
    };

    const onRemoveAction = (actionToRemove: Action) => {
      const updatedActions = (formikProps.values.actions || []).filter(
        (action) => action.id !== actionToRemove.id
      );
      formikProps.setFieldValue("actions", updatedActions);
    };

    const onActionEditIconClicked = (action: Action) => {
      setSelectedAction(cloneAction(action));
      setIsActionFormOpen(true);
    };
    const onActionRemoveIconClicked = (action: Action) => {
      setActionSelectedToRemove(action);
      setIsRemoveDialogOpen(true);
    };

    const onConfirmRemoveAction = () => {
      if (!actionSelectedToRemove) return;
      const updatedActions = (formikProps.values.actions || []).filter(
        (action) => action.id !== actionSelectedToRemove.id
      );
      formikProps.setFieldValue("actions", updatedActions);
      setIsRemoveDialogOpen(false);
      setActionSelectedToRemove(null);
    };

    const onToggleRemoveDialog = () => {
      setIsRemoveDialogOpen(!isRemoveDialogOpen);
      if (isRemoveDialogOpen) setActionSelectedToRemove(null);
    };

    const renderActionForm = () => (
      <FullScreenDialog
        onClose={onToggleActionForm}
        isOpen={isActionFormOpen}
        title={selectedAction ? "Edit Action" : "New Action"}
      >
        <ActionForm
          onToggleFullScreenDialog={onToggleActionForm}
          onCreateItem={onCreateAction}
          onEditItem={onEditAction}
          item={selectedAction}
          habitId={formikProps.values.id}
          communityId={formikProps.values.communityId}
          mode={selectedAction ? "edit" : "create"}
        />
      </FullScreenDialog>
    );

    const renderActionTable = () => (
      <div>
        <ImportActions
          prevActions={formikProps.values.actions}
          habitId={formikProps.values.id}
          setActions={(actions) =>
            formikProps.setFieldValue(
              "actions",
              (actions || []).map(cloneAction)
            )
          }
        />
        <ActionButton
          action={onToggleActionForm}
          title={"New Action"}
          withIcon
          disabled={false}
        />
        <Table
          canBeEdited
          onEditIconClicked={onActionEditIconClicked}
          onRemoveIconClicked={onActionRemoveIconClicked}
          updatePageIndex={() => {}}
          currentPage={0}
          tabConfig={actionTabConfig.tabConfig}
          dataset={formikProps.values.actions || []}
          canBeRemoved
        />
      </div>
    );
    const renderRemoveDialog = () => (
      <Dialog
        description="If you delete this action, it will be removed permanently and cannot be recovered."
        title="Are you sure you want to delete this Action?"
        negativeAction={onToggleRemoveDialog}
        onCloseDialog={onToggleRemoveDialog}
        positiveAction={onConfirmRemoveAction}
        isOpen={isRemoveDialogOpen}
        positiveText="YES"
        negativeText="NO"
      />
    );

    const actionSubactionRows = (formikProps.values.actions || []).flatMap(
      (action) =>
        (action.subactions || []).map((subaction, subactionIndex) => ({
          id: subaction.id || `${action.id}-${subactionIndex}`,
          actionId: action.id,
          actionName: action.name,
          subactionIndex,
          name: subaction.name,
          description: subaction.description,
          actionType: subaction.actionType,
          backgroundImageURL: subaction.backgroundImageURL,
          videoURL: subaction.videoURL,
          subaction,
          isStandalone: false,
        }))
    );

    const standaloneSubactionRows = (
      formikProps.values.standaloneSubactions || []
    ).map((subaction, subactionIndex) => ({
      id: subaction.id || `standalone-${subactionIndex}`,
      actionId: undefined,
      actionName: "Standalone",
      subactionIndex,
      name: subaction.name,
      description: subaction.description,
      actionType: subaction.actionType,
      backgroundImageURL: subaction.backgroundImageURL,
      videoURL: subaction.videoURL,
      subaction,
      isStandalone: true,
    }));

    const subactionRows = [...actionSubactionRows, ...standaloneSubactionRows];

    const onOpenSubactionForm = ({
      mode,
      actionId,
      subaction,
      subactionIndex,
      isStandalone,
    }: {
      mode: "create" | "edit";
      actionId?: string;
      subaction?: Subaction;
      subactionIndex?: number;
      isStandalone?: boolean;
    }) => {
      setSubactionFormMode(mode);
      setSelectedSubaction(subaction ? { ...subaction } : null);
      if (mode === "edit") {
        setSelectedSubactionContext({
          actionId,
          subactionId: subaction?.id,
          subactionIndex,
          isStandalone,
        });
      } else {
        setSelectedSubactionContext(null);
      }
      setIsSubactionFormOpen(true);
    };

    const onCloseSubactionForm = () => {
      setIsSubactionFormOpen(false);
      setSelectedSubaction(null);
      setSelectedSubactionContext(null);
      setSubactionFormMode("create");
    };

    const onSubactionEditIconClicked = (row) => {
      onOpenSubactionForm({
        mode: "edit",
        actionId: row.actionId,
        subaction: row.subaction,
        subactionIndex: row.subactionIndex,
        isStandalone: row.isStandalone,
      });
    };

    const onSubactionRemoveIconClicked = (row) => {
      setSubactionSelectedToRemove({
        actionId: row.actionId,
        subactionId: row.subaction?.id,
        subactionIndex: row.subactionIndex,
        isStandalone: row.isStandalone,
      });
      setIsSubactionRemoveDialogOpen(true);
    };

    const onToggleSubactionRemoveDialog = () => {
      setIsSubactionRemoveDialogOpen(!isSubactionRemoveDialogOpen);
      if (isSubactionRemoveDialogOpen) setSubactionSelectedToRemove(null);
    };

    const onConfirmRemoveSubaction = () => {
      if (!subactionSelectedToRemove) return;
      if (subactionSelectedToRemove.isStandalone) {
        const updatedStandalone = (
          formikProps.values.standaloneSubactions || []
        ).filter((subaction, index) => {
          if (subactionSelectedToRemove.subactionId) {
            return subaction.id !== subactionSelectedToRemove.subactionId;
          }
          return index !== subactionSelectedToRemove.subactionIndex;
        });
        formikProps.setFieldValue(
          "standaloneSubactions",
          updatedStandalone.map(cloneSubaction)
        );
      } else {
        const updatedActions = (formikProps.values.actions || []).map(
          (action) => {
            if (action.id !== subactionSelectedToRemove.actionId) return action;
            const subactions = (action.subactions || []).filter(
              (subaction, index) => {
                if (subactionSelectedToRemove.subactionId) {
                  return subaction.id !== subactionSelectedToRemove.subactionId;
                }
                return index !== subactionSelectedToRemove.subactionIndex;
              }
            );
            return { ...action, subactions };
          }
        );
        formikProps.setFieldValue(
          "actions",
          updatedActions.map(cloneAction)
        );
      }
      setIsSubactionRemoveDialogOpen(false);
      setSubactionSelectedToRemove(null);
    };

    const renderSubactionForm = () => {
      const STANDALONE_KEY = "__standalone__";
      const emptySubaction: Subaction = {
        id: "",
        name: "",
        description: "",
        actionType: ActionType.CUSTOM,
        createdAt: new Date().toISOString(),
      };

      const initialActionId =
        selectedSubactionContext?.isStandalone
          ? STANDALONE_KEY
          : selectedSubactionContext?.actionId ||
            formikProps.values.actions?.[0]?.id ||
            STANDALONE_KEY;

      const initialSubaction = selectedSubaction
        ? {
            ...selectedSubaction,
            actionType: selectedSubaction.actionType || ActionType.CUSTOM,
            createdAt:
              selectedSubaction.createdAt || new Date().toISOString(),
          }
        : emptySubaction;

      return (
        <FullScreenDialog
          onClose={onCloseSubactionForm}
          isOpen={isSubactionFormOpen}
          title={
            subactionFormMode === "edit" ? "Edit Subaction" : "New Subaction"
          }
        >
          <Formik
            initialValues={{
              parentActionId: initialActionId,
              subactions: [initialSubaction],
            }}
            onSubmit={(values, { setSubmitting }) => {
              const [subaction] = values.subactions || [];
              const parentActionId = values.parentActionId;

              if (!parentActionId || !subaction) {
                setSubmitting(false);
                return;
              }

              const isTargetStandalone = parentActionId === STANDALONE_KEY;
              const isOriginalStandalone = !!selectedSubactionContext?.isStandalone;
              const normalizedSubaction: Subaction = {
                ...subaction,
                ...(isTargetStandalone ? {} : { actionId: parentActionId }),
              };
              if (isTargetStandalone) {
                delete (normalizedSubaction as any).actionId;
              }

              let updatedStandalone = [
                ...(formikProps.values.standaloneSubactions || []),
              ];

              const removeFromStandalone = () => {
                updatedStandalone = updatedStandalone.filter(
                  (existing, index) => {
                    if (selectedSubactionContext?.subactionId) {
                      return (
                        existing.id !== selectedSubactionContext.subactionId
                      );
                    }
                    return index !== selectedSubactionContext?.subactionIndex;
                  }
                );
              };

              if (isOriginalStandalone && !isTargetStandalone) {
                removeFromStandalone();
              }

              if (isTargetStandalone) {
                if (subactionFormMode === "edit" && isOriginalStandalone) {
                  if (selectedSubactionContext?.subactionId) {
                    updatedStandalone = updatedStandalone.map((existing) =>
                      existing.id === selectedSubactionContext.subactionId
                        ? normalizedSubaction
                        : existing
                    );
                  } else if (
                    typeof selectedSubactionContext?.subactionIndex === "number"
                  ) {
                    updatedStandalone = updatedStandalone.map((existing, index) =>
                      index === selectedSubactionContext.subactionIndex
                        ? normalizedSubaction
                        : existing
                    );
                  } else {
                    const withoutExisting = normalizedSubaction.id
                      ? updatedStandalone.filter(
                          (existing) => existing.id !== normalizedSubaction.id
                        )
                      : updatedStandalone;
                    updatedStandalone = [...withoutExisting, normalizedSubaction];
                  }
                } else {
                  const withoutExisting = normalizedSubaction.id
                    ? updatedStandalone.filter(
                        (existing) => existing.id !== normalizedSubaction.id
                      )
                    : updatedStandalone;
                  updatedStandalone = [...withoutExisting, normalizedSubaction];
                }
              }

              const updatedActions = (formikProps.values.actions || []).map(
                (action) => {
                  let nextSubactions = [...(action.subactions || [])];
                  const isOriginal =
                    !isOriginalStandalone &&
                    selectedSubactionContext &&
                    action.id === selectedSubactionContext.actionId;
                  const isTarget =
                    !isTargetStandalone && action.id === parentActionId;
                  const isSameAction = isOriginal && isTarget;

                  if (isOriginal && !isSameAction) {
                    if (selectedSubactionContext?.subactionId) {
                      nextSubactions = nextSubactions.filter(
                        (existing) =>
                          existing.id !== selectedSubactionContext.subactionId
                      );
                    } else if (
                      typeof selectedSubactionContext?.subactionIndex ===
                      "number"
                    ) {
                      nextSubactions = nextSubactions.filter(
                        (_, index) =>
                          index !== selectedSubactionContext.subactionIndex
                      );
                    }
                  }

                  if (isTarget) {
                    if (subactionFormMode === "edit" && isSameAction) {
                      if (selectedSubactionContext?.subactionId) {
                        nextSubactions = nextSubactions.map((existing) =>
                          existing.id === selectedSubactionContext.subactionId
                            ? normalizedSubaction
                            : existing
                        );
                      } else if (
                        typeof selectedSubactionContext?.subactionIndex ===
                        "number"
                      ) {
                        nextSubactions = nextSubactions.map((existing, index) =>
                          index === selectedSubactionContext.subactionIndex
                            ? normalizedSubaction
                            : existing
                        );
                      } else {
                        nextSubactions = [
                          ...nextSubactions.filter(
                            (existing) =>
                              existing.id !== normalizedSubaction.id
                          ),
                          normalizedSubaction,
                        ];
                      }
                    } else {
                      const withoutExisting = normalizedSubaction.id
                        ? nextSubactions.filter(
                            (existing) =>
                              existing.id !== normalizedSubaction.id
                          )
                        : nextSubactions;
                      nextSubactions = [
                        ...withoutExisting,
                        normalizedSubaction,
                      ];
                    }
                  }

                  return { ...action, subactions: nextSubactions };
                }
              );

              formikProps.setFieldValue(
                "actions",
                updatedActions.map(cloneAction)
              );
              formikProps.setFieldValue(
                "standaloneSubactions",
                updatedStandalone.map(cloneSubaction)
              );
              setIsSubactionFormOpen(false);
              setSelectedSubaction(null);
              setSelectedSubactionContext(null);
              setSubactionFormMode("create");
              setSubmitting(false);
            }}
            enableReinitialize
          >
            {({ values, setFieldValue, submitForm, isSubmitting }) => (
              <Form>
                <ActionFormButton
                  onClick={submitForm}
                  disabled={isSubmitting}
                  entity="Subaction"
                  mode={subactionFormMode}
                />
                <Box mb={2}>
                  <FormControl className={classes.formControl} fullWidth>
                    <InputLabel id="subaction-action-label">
                      Parent Action
                    </InputLabel>
                    <Select
                      labelId="subaction-action-label"
                      value={values.parentActionId}
                      onChange={(event) =>
                        setFieldValue("parentActionId", event.target.value)
                      }
                    >
                      <MenuItem value={STANDALONE_KEY}>Standalone</MenuItem>
                      {(formikProps.values.actions || []).map((action) => (
                        <MenuItem key={action.id} value={action.id}>
                          {action.name || action.id}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                {values.subactions && values.subactions[0] && (
                  <SubactionForm
                    index={0}
                    subaction={values.subactions[0]}
                    setFieldValue={setFieldValue}
                    showRemove={false}
                  />
                )}
              </Form>
            )}
          </Formik>
        </FullScreenDialog>
      );
    };

    const renderSubactionTable = () => {
      const query = subactionSearch.trim().toLowerCase();
      const filteredSubactionRows = query
        ? subactionRows.filter((row) =>
            [row.name, row.description, row.actionName, row.actionType].some(
              (field) => String(field || "").toLowerCase().includes(query)
            )
          )
        : subactionRows;

      const pageCount = Math.max(
        1,
        Math.ceil(filteredSubactionRows.length / subactionRowsPerPage)
      );
      const safePage = Math.min(subactionPage, pageCount - 1);

      return (
        <div>
          <ImportSubactions
            habitId={formikProps.values.id}
            communityId={formikProps.values.communityId}
            standaloneSubactions={formikProps.values.standaloneSubactions || []}
            setStandaloneSubactions={(subactions) =>
              formikProps.setFieldValue(
                "standaloneSubactions",
                (subactions || []).map(cloneSubaction)
              )
            }
          />
          <ActionButton
            action={() => onOpenSubactionForm({ mode: "create" })}
            title={"New Subaction"}
            withIcon
            disabled={(formikProps.values.actions || []).length === 0}
          />
          <Box mt={1} mb={1}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder="Search subactions by name, description, action, or type"
              value={subactionSearch}
              onChange={(event) => {
                setSubactionSearch(event.target.value);
                setSubactionPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            {query && (
              <Box mt={0.5}>
                <Typography variant="caption" color="textSecondary">
                  Showing {filteredSubactionRows.length} of {subactionRows.length}
                </Typography>
              </Box>
            )}
          </Box>
          <Table
            canBeEdited
            onEditIconClicked={onSubactionEditIconClicked}
            onRemoveIconClicked={onSubactionRemoveIconClicked}
            updatePageIndex={setSubactionPage}
            currentPage={safePage}
            itemsPerPage={subactionRowsPerPage}
            setItemsPerPage={(rows: number) => {
              setSubactionRowsPerPage(Number(rows) || 10);
              setSubactionPage(0);
            }}
            tabConfig={subactionTabConfig}
            dataset={filteredSubactionRows}
            canBeRemoved
          />
        </div>
      );
    };

    const renderSubactionRemoveDialog = () => (
      <Dialog
        description="If you delete this subaction, it will be removed permanently and cannot be recovered."
        title="Are you sure you want to delete this Subaction?"
        negativeAction={onToggleSubactionRemoveDialog}
        onCloseDialog={onToggleSubactionRemoveDialog}
        positiveAction={onConfirmRemoveSubaction}
        isOpen={isSubactionRemoveDialogOpen}
        positiveText="YES"
        negativeText="NO"
      />
    );

    const renderTabs = () => (
      <Box mb={2}>
        <Tabs
          onChange={(_, currentTab) => {
            setCurrentTabIndex(currentTab);
          }}
          value={currentTabIndex}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab
            label={`Actions (${(formikProps.values.actions || []).length})`}
          />
          <Tab label={`Subactions (${subactionRows.length})`} />
        </Tabs>
      </Box>
    );

    const renderLearningPlanSelect = () => (
      <FormControl className={classes.formControl} fullWidth>
        <InputLabel id="learning-plans-label">
          Related Learning Plans
        </InputLabel>
        <Select
          labelId="learning-plans-label"
          multiple
          value={formikProps.values.learningPlansIds}
          onChange={(event) => {
            formikProps.setFieldValue("learningPlansIds", event.target.value);
          }}
          renderValue={(selected) => (
            <Box className={classes.chips}>
              {(selected as string[]).map((value) => {
                const plan: Plan = learningPlans.find((p) => p.id === value);
                return (
                  <Chip
                    key={value}
                    label={plan ? plan.title : value}
                    className={classes.chip}
                  />
                );
              })}
            </Box>
          )}
        >
          {learningPlans.map((plan) => (
            <MenuItem key={plan.id} value={plan.id}>
              {plan.title}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );

    const renderPracticeTypeSelect = () => (
      <Row>
        <RowItem>
          <FormControl className={classes.formControl} fullWidth>
            <InputLabel id="practice-type-label">Practice Type</InputLabel>
            <Select
              labelId="practice-type-label"
              id="practiceType"
              disabled={mode === "detail"}
              value={formikProps.values.practiceType || PracticeType.DEFAULT}
              onChange={(event) => {
                const nextPracticeType = Number(event.target.value);
                formikProps.setFieldValue("practiceType", nextPracticeType);

                if (nextPracticeType !== PracticeType.DIET) {
                  formikProps.setFieldValue("defaultDietType", "");
                }

                if (
                  nextPracticeType === PracticeType.EXERCISE &&
                  !formikProps.values.workoutBuilder
                ) {
                  formikProps.setFieldValue(
                    "workoutBuilder",
                    cloneWorkoutBuilderConfig()
                  );
                }
              }}
            >
              {practiceTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </RowItem>
      </Row>
    );

    console.log("Learning Plans", learningPlans);
    return (
      <Form>
        <StandaloneSubactionsLoader
          habitId={formikProps.values.id}
          communityId={formikProps.values.communityId}
        />
        <ActionsLoader
          habitId={formikProps.values.id}
          communityId={formikProps.values.communityId}
        />
        <Section>
          {renderSectionTitle("Habit Information")}
          {renderRowWithTwoItems(
            getRowItemObject(
              "Habit Name",
              "Enter the habit name",
              "text",
              "habitName"
            ),
            getRowItemObject(
              "Habit Description",
              "Enter the habit description",
              "textarea",
              "habitDescription"
            ),
            { ...formikProps, mode }
          )}
          {renderPracticeTypeSelect()}
          {renderRowWithSingleItem(
            getRowItemObject(
              "Position",
              "Order in the list (lower shows first, leave blank for default)",
              "number",
              "position"
            ),
            { ...formikProps, mode }
          )}
          {formikProps.values.practiceType === PracticeType.DIET &&
            renderRowWithSingleItem(
              getRowItemObject(
                "Default Diet Type",
                "Enter the default diet type",
                "text",
                "defaultDietType"
              ),
              { ...formikProps, mode }
            )}
          {renderRowWithSingleCheckbox(
            getRowItemObject(
              "Status",
              "Select the habit status",
              "checkbox",
              "status"
            ),

            { ...formikProps, mode }
          )}
          {renderLearningPlanSelect()}
        </Section>

        <Section>
          {renderSectionTitle("Background Image")}

          <Field name="backgroundImageURL">
            {({ field, form }) => (
              <ImageUpload
                field={field}
                form={form}
                actionId={item?.id}
                reference={"habits"}
              />
            )}
          </Field>
        </Section>

        <Section>
          {renderSectionTitle("AI Generation")}
          <Row>
            <RowItem>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!formikProps.values.useLibrary}
                    onChange={(event) =>
                      formikProps.setFieldValue(
                        "useLibrary",
                        event.target.checked
                      )
                    }
                    color="primary"
                  />
                }
                label="Use Library"
              />
            </RowItem>
          </Row>
          {renderRowWithSingleItem(
            getRowItemObject(
              "Generation Plan Prompt",
              "Prompt for multi-day plan generation",
              "textarea",
              "generationPlanPrompt"
            ),
            { ...formikProps, mode }
          )}
        </Section>

        {formikProps.values.practiceType === PracticeType.EXERCISE && (
          <Section>
            <WorkoutBuilderConfig
              value={formikProps.values.workoutBuilder}
              onChange={(nextValue) =>
                formikProps.setFieldValue(
                  "workoutBuilder",
                  cloneWorkoutBuilderConfig(nextValue)
                )
              }
              disabled={mode === "detail"}
              uploadContextId={
                formikProps.values.id || workoutBuilderUploadIdRef.current
              }
            />
          </Section>
        )}

        <Section>
          {renderSectionTitle("Actions & Subactions")}
          {renderTabs()}
          {currentTabIndex === 0 ? renderActionTable() : renderSubactionTable()}
          {renderActionForm()}
          {renderRemoveDialog()}
          {renderSubactionForm()}
          {renderSubactionRemoveDialog()}
        </Section>
        <ActionFormButton
          onChageFormToEditMode={onChageFormToEditMode}
          onClick={formikProps.submitForm}
          onRemoveItem={onRemoveItem}
          disabled={formikProps.isSubmitting}
          entity="Habit"
          canBeRemoved
          mode={mode}
        />
      </Form>
    );
  };

  return (
    <Wrapper>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, { setSubmitting }) => {
          console.log("Values", values);
          const payload: Habit = {
            ...values,
            position:
              (values.position as any) === "" || values.position == null
                ? undefined
                : Number(values.position),
            useLibrary: !!values.useLibrary,
            generationPlanPrompt: values.generationPlanPrompt || "",
            defaultDietType:
              values.practiceType === PracticeType.DIET
                ? values.defaultDietType || ""
                : "",
            workoutBuilder: values.workoutBuilder
              ? cloneWorkoutBuilderConfig(values.workoutBuilder)
              : undefined,
          };
          const properCallback = mode === "edit" ? onEditItem : onCreateItem;
          properCallback(payload);
          setSubmitting(false);
        }}
        enableReinitialize
      >
        {renderForm}
      </Formik>
    </Wrapper>
  );
};

export default HabitForm;
